import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface Category {
  id: number;
  name: string;
  characters?: string[]; // Deprecated - solo para retrocompatibilidad
  characterCount?: number; // Nuevo campo seguro
}

interface CategorySelectorProps {
  categories: Category[];
  selectedCategoryId: number | null;
  onSelectCategory: (categoryId: number) => void;
  disabled?: boolean;
}

const ITEMS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 300; // ms

const CategorySelector: React.FC<CategorySelectorProps> = ({
  categories,
  selectedCategoryId,
  onSelectCategory,
  disabled = false
}) => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [isOpen, setIsOpen] = useState(false);

  // Debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Ordenar alfabéticamente y filtrar categorías
  const filteredAndSortedCategories = useMemo(() => {
    // Ordenar alfabéticamente (A-Z)
    const locale = i18n.language || 'es';
    const sorted = [...categories].sort((a, b) => 
      a.name.localeCompare(b.name, locale, { sensitivity: 'base' })
    );

    // Filtrar por término de búsqueda (búsqueda por coincidencia parcial)
    if (debouncedSearchTerm.trim() === '') {
      return sorted;
    }

    const searchLower = debouncedSearchTerm.toLowerCase();
    return sorted.filter(cat => 
      cat.name.toLowerCase().includes(searchLower)
    );
  }, [categories, debouncedSearchTerm, i18n.language]);

  // Categorías visibles según la paginación
  const visibleCategories = useMemo(() => {
    return filteredAndSortedCategories.slice(0, visibleCount);
  }, [filteredAndSortedCategories, visibleCount]);

  // Resetear paginación cuando cambia el término de búsqueda
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [debouncedSearchTerm]);

  const handleLoadMore = useCallback(() => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSelectCategory = useCallback((categoryId: number) => {
    onSelectCategory(categoryId);
    setIsOpen(false);
  }, [onSelectCategory]);

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);
  const hasMore = visibleCount < filteredAndSortedCategories.length;

  return (
    <div className="relative">
      {/* Botón de selección */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full text-left px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-900 
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400'}
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900/40' : 'border-gray-300 dark:border-gray-700'}
          transition-all duration-200 flex items-center justify-between`}
      >
        <span className="font-medium text-gray-700 dark:text-gray-200">
            {selectedCategory ? selectedCategory.name : t('categorySelector.selectPlaceholder')}
        </span>
        <span className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg">
          {/* Buscador */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-800">
            <input
              type="text"
              placeholder={t('categorySelector.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 rounded-md 
                focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent
                text-sm"
              autoFocus
            />
          </div>

          {/* Lista de categorías con scroll */}
          <div className="max-h-80 overflow-y-auto">
            {visibleCategories.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                {t('categorySelector.noResults')}
              </div>
            ) : (
              <>
                {visibleCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleSelectCategory(category.id)}
                    className={`w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-gray-800 
                      transition-colors duration-150 border-b border-gray-100 dark:border-gray-800 last:border-b-0
                      ${category.id === selectedCategoryId ? 'bg-blue-100 dark:bg-blue-900/30 font-semibold text-blue-700 dark:text-blue-200' : 'text-gray-700 dark:text-gray-200'}
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <span>{category.name}</span>
                      {category.id === selectedCategoryId && (
                        <span className="text-blue-600 dark:text-blue-300 text-sm">✓</span>
                      )}
                    </div>
                    {/* Usar characterCount (nuevo) o fallback a characters.length (deprecated) */}
                    {(category.characterCount !== undefined && category.characterCount > 0) || 
                     (category.characters && category.characters.length > 0) ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('categorySelector.charactersCount', {
                          count: category.characterCount ?? category.characters?.length ?? 0
                        })}
                      </div>
                    ) : null}
                  </button>
                ))}

                {/* Botón "Mostrar más" */}
                {hasMore && (
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    className="w-full px-4 py-3 text-center text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-gray-800 
                      font-medium text-sm transition-colors duration-150 border-t border-gray-200 dark:border-gray-800"
                  >
                    {t('categorySelector.showMore', {
                      count: filteredAndSortedCategories.length - visibleCount
                    })}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Información de resultados */}
          {filteredAndSortedCategories.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 text-center">
              {t('categorySelector.showing', {
                visible: visibleCategories.length,
                total: filteredAndSortedCategories.length
              })}
              {debouncedSearchTerm && ` ${t('categorySelector.filteredSuffix', { all: categories.length })}`}
            </div>
          )}
        </div>
      )}

      {/* Overlay para cerrar al hacer clic fuera */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CategorySelector;
